<template>
  <el-autocomplete
    :model-value="modelValue"
    :placeholder="placeholder"
    :fetch-suggestions="fetchSuggestions"
    clearable
    style="width: 100%"
    :trigger-on-focus="triggerOnFocus"
    @update:model-value="$emit('update:modelValue', $event)"
    @select="(item) => $emit('select', item)"
  >
    <template #default="{ item }">
      <div class="holder-suggest-item">
        <span class="holder-name">{{ item.label ?? item.value }}</span>
        <el-tag v-if="item.isCore" size="small" type="danger">核心</el-tag>
        <el-tag v-else size="small" type="info">普通</el-tag>
      </div>
    </template>
  </el-autocomplete>
</template>

<script setup>
/* eslint-disable no-undef */
import axios from 'axios'

const props = defineProps({
  /** 类型：core=核心股东，raw=普通股东，all=核心+普通（代持/特殊价格用） */
  type: { type: String, required: true, validator: v => ['core', 'raw', 'all'].includes(v) },
  placeholder: { type: String, default: '' },
  modelValue: { type: String, default: '' },
  /** 是否在获得焦点时请求一次（空关键词） */
  triggerOnFocus: { type: Boolean, default: false },
  /** 绑定值用 API 的 value(id/账户) 还是 label(名称)。筛选用 label，表单提交用 value */
  valueKey: { type: String, default: 'value', validator: v => ['value', 'label'].includes(v) },
})

defineEmits(['update:modelValue', 'select'])

let debounceTimer = null

async function fetchSuggestions(queryString, cb) {
  const keyword = (queryString || '').trim()
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    try {
      if (props.type === 'all') {
        const [coreRes, rawRes] = await Promise.all([
          axios.get('/api/core_shareholder/search/', { params: { keyword } }),
          axios.get('/api/unique_shareholder/search/', { params: { keyword } }),
        ])
        const coreList = coreRes.data.status === 'ok' ? (coreRes.data.data || []) : []
        const rawList = rawRes.data.status === 'ok' ? (rawRes.data.data || []) : []
        const coreNames = new Set(coreList.map(i => (i.label || i.name || '').trim()))
        const suggestions = []
        const useLabel = props.valueKey === 'label'
        coreList.forEach(item => {
          const label = (item.label || item.name || '').trim()
          const apiVal = (item.value !== undefined && item.value !== '') ? item.value : label
          if (label) suggestions.push({ ...item, value: useLabel ? label : apiVal, label, isCore: true })
        })
        rawList.forEach(item => {
          const name = (item.name || item.label || '').trim()
          if (name && !coreNames.has(name)) {
            const apiVal = (item.value !== undefined && item.value !== '') ? item.value : name
            suggestions.push({ ...item, value: useLabel ? name : apiVal, label: item.label || name, isCore: false })
          }
        })
        cb(suggestions.slice(0, 30))
        return
      }
      const url = props.type === 'core'
        ? '/api/core_shareholder/search/'
        : '/api/unique_shareholder/search/'
      const res = await axios.get(url, { params: { keyword } })
      if (res.data.status !== 'ok') {
        cb([])
        return
      }
      const list = res.data.data || []
      const useLabel = props.valueKey === 'label'
      const suggestions = list.map(item => {
        if (props.type === 'core') {
          const label = item.label || item.name || ''
          const apiVal = (item.value !== undefined && item.value !== '') ? item.value : label
          const value = useLabel ? label : apiVal
          return { ...item, value, label, isCore: true }
        }
        const label = item.label || item.name || ''
        const apiVal = (item.value !== undefined && item.value !== '') ? item.value : (item.name || label)
        const value = useLabel ? (item.name || label) : apiVal
        return { ...item, value, label, isCore: false }
      })
      cb(suggestions)
    } catch (e) {
      cb([])
    }
  }, 200)
}
</script>

<style scoped>
.holder-suggest-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.holder-suggest-item .holder-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
